import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Breach Notifications — admin SOC dashboard listing data-breach
// incidents (not-yet-closed by default) for the Large Projects module.
//
// Consumes besthand-api `listBreachNotifications(status, minSeverity, limit)`
// (returns `breachNotifications`) through the `/lp-graphql` proxy via `lpApi`.
// Admin-gated server-side (requireAdmin).
// ============================================================================

const Q_BREACH = `
  query ($status: String, $minSeverity: String, $limit: Int) {
    listBreachNotifications(status: $status, minSeverity: $minSeverity, limit: $limit) {
      status errorMessage
      breachNotifications {
        id title description severity status detectedAt contained closedAt
        affectedUserCount mitigations reportedToAuthority reportedAt
        createdBy createdAt
      }
    }
  }
`;

// closeBreachNotification(breachNotificationId!, targetStatus!, notes)
// targetStatus ∈ INVESTIGATING | CONTAINED | CLOSED (we expose CONTAINED + CLOSED)
const M_CLOSE = `
  mutation ($breachNotificationId: String!, $targetStatus: String!, $notes: String) {
    closeBreachNotification(breachNotificationId: $breachNotificationId, targetStatus: $targetStatus, notes: $notes) {
      status errorMessage
      breachNotification { id status contained closedAt }
    }
  }
`;

const SEVERITIES = [
  { value: '', label: 'Any severity' },
  { value: 'LOW', label: 'Low+' },
  { value: 'MEDIUM', label: 'Medium+' },
  { value: 'HIGH', label: 'High+' },
  { value: 'CRITICAL', label: 'Critical only' },
];

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  select: { padding: '9px 12px', border: '1px solid #d8dde2', borderRadius: 9, fontSize: 14, fontWeight: 600, outline: 'none', background: '#fff', cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  desc: { fontSize: 14, color: '#0f1214', marginTop: 10, lineHeight: 1.5 },
  badge: (bg, fg) => ({ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' }),
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btnAmber: { background: '#fff', color: '#946200', border: '1px solid #f0d79a', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const sevBadge = (sev) => {
  const map = {
    LOW: ['#eef2f4', '#5a6168'],
    MEDIUM: ['#fff3d6', '#946200'],
    HIGH: ['#ffe0d6', '#b3490f'],
    CRITICAL: ['#fde7e8', '#c2151c'],
  };
  return map[sev] || ['#eef2f4', '#5a6168'];
};
const statusBadge = (st) => {
  const map = {
    OPEN: ['#fde7e8', '#c2151c'],
    CONTAINED: ['#fff3d6', '#946200'],
    CLOSED: ['#e7f6ec', '#137a37'],
  };
  return map[st] || ['#eef2f4', '#5a6168'];
};
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function Breach() {
  const [items, setItems] = useState([]);
  const [minSeverity, setMinSeverity] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async (minSeverityArg) => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_BREACH, { status: null, minSeverity: minSeverityArg || null, limit: 100 });
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listBreachNotifications;
        if (env && env.status === 200) setItems(env.breachNotifications || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load breach notifications: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(minSeverity); }, [load, minSeverity]);

  const run = async (key, query, variables, opName, okText) => {
    setBusyId(key);
    setMsg(null);
    try {
      const res = await lpApi(query, variables);
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data[opName];
      if (env && env.status >= 200 && env.status < 300) {
        setMsg({ ok: okText });
        await load(minSeverity);
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusyId('');
  };

  const transition = (b, targetStatus, label) => {
    const notes = typeof window !== 'undefined' ? window.prompt(`Mark incident #${b.id} as ${label}.\n\nOptional notes to append to mitigations (leave blank to skip):`) : '';
    if (notes === null) return; // cancelled
    run(`b${b.id}`, M_CLOSE, { breachNotificationId: String(b.id), targetStatus, notes: notes.trim() ? notes.trim() : null }, 'closeBreachNotification', `Incident #${b.id} marked ${label}.`);
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Breach Notifications</h2>
      <div style={S.hint}>
        Large Projects — data-breach incident log (open and contained by default), newest first. Filter by
        minimum severity.
      </div>

      <div style={S.toolbar}>
        <select style={S.select} value={minSeverity} onChange={(e) => setMinSeverity(e.target.value)} aria-label="Minimum severity">
          {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button style={S.btnGhost} onClick={() => load(minSeverity)} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{items.length} shown</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        items.length === 0 ? (
          <div style={S.empty}>No breach notifications. 🎉</div>
        ) : items.map((b) => {
          const [sevBg, sevFg] = sevBadge(b.severity);
          const [stBg, stFg] = statusBadge(b.status);
          return (
            <div style={S.card} key={b.id}>
              <div style={S.rowTop}>
                <div>
                  <p style={S.name}>{b.title || `Incident #${b.id}`}</p>
                  <div style={S.meta}>
                    {b.detectedAt ? `Detected ${fmtDate(b.detectedAt)}` : ''}
                    {b.affectedUserCount != null ? ` · ${b.affectedUserCount} users affected` : ''}
                    {b.reportedToAuthority ? ` · Reported to authority${b.reportedAt ? ` ${fmtDate(b.reportedAt)}` : ''}` : ''}
                    {b.createdBy ? ` · by ${b.createdBy}` : ''}
                  </div>
                  {b.description ? <div style={S.desc}>{b.description}</div> : null}
                  {b.mitigations ? <div style={S.meta}>Mitigations: {b.mitigations}</div> : null}
                  {b.contained ? <div style={S.meta}>Contained {fmtDate(b.contained)}</div> : null}
                  {b.closedAt ? <div style={S.meta}>Closed {fmtDate(b.closedAt)}</div> : null}
                </div>
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <span style={S.badge(sevBg, sevFg)}>{b.severity || '—'}</span>
                  <span style={S.badge(stBg, stFg)}>{b.status || '—'}</span>
                </div>
              </div>
              {b.status !== 'CLOSED' && (
                <div style={S.actions}>
                  {b.status !== 'CONTAINED' && (
                    <button style={S.btnAmber} disabled={busyId === `b${b.id}`} onClick={() => transition(b, 'CONTAINED', 'contained')}>
                      {busyId === `b${b.id}` ? '…' : 'Mark contained'}
                    </button>
                  )}
                  <button style={S.btn} disabled={busyId === `b${b.id}`} onClick={() => transition(b, 'CLOSED', 'closed')}>
                    {busyId === `b${b.id}` ? '…' : 'Mark closed'}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

export default Breach;
